import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ResearchLayout from '../../components/ResearchLayout';
import {
  getQuestion,
  updateQuestion,
  deleteQuestion,
  forceDeleteQuestion,
  rawDeleteQuestion
} from '../../services/questionService';
import api from '../../services/api'; // Import your API service

const ProgramAdminEditQuestion = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [deleteMethod, setDeleteMethod] = useState('normal');
  
  const [formData, setFormData] = useState({
    text: '',
    type: 'mcq',
    category: 'General',
    choices: ['', '', '', ''],
  });
  
  const [protocols, setProtocols] = useState([]);
  const [selectedProtocols, setSelectedProtocols] = useState([]);
  
  useEffect(() => {
    loadQuestion();
  }, [id]);
  
  useEffect(() => {
    const loadProtocols = async () => {
      const res = await api.get('/protocols');
      setProtocols(res.data.protocols || []);
    };
    loadProtocols();
  }, []);
  
  const loadQuestion = async () => {
    try {
      setLoading(true);
      const response = await getQuestion(id);
      // PATCH: Use response.data.question for Axios
      const question = response.data?.question;
      if (!question) {
        throw new Error('Question not found');
      }
      setFormData({
        text: question.text || '',
        type: question.type || 'mcq',
        category: question.category || 'General',
        choices: question.choices && question.choices.length > 0 
          ? question.choices 
          : ['', '', '', ''],
      });
      setSelectedProtocols(question.protocols ? question.protocols.map(p => p.id) : []);
    } catch (error) {
      console.error('Error loading question:', error);
      setError('Failed to load question. ' + (error.message || ''));
    } finally {
      setLoading(false);
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleChoiceChange = (index, value) => {
    const updatedChoices = [...formData.choices];
    updatedChoices[index] = value;
    setFormData(prev => ({
      ...prev,
      choices: updatedChoices
    }));
  };
  
  const addChoice = () => {
    if (formData.choices.length < 8) {
      setFormData(prev => ({
        ...prev,
        choices: [...prev.choices, '']
      }));
    }
  };
  
  const removeChoice = (index) => {
    if (formData.choices.length > 2) {
      const updatedChoices = formData.choices.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        choices: updatedChoices
      }));
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      setError('');
      
      // Validate the question data
      if (!formData.text.trim()) {
        throw new Error('Question text is required');
      }
      
      if (formData.type === 'mcq') {
        const validChoices = formData.choices.filter(c => c.trim());
        if (validChoices.length < 2) {
          throw new Error('Multiple choice questions must have at least 2 options');
        }
      }
      // Prepare question data
      const questionData = {
        text: formData.text.trim(),
        type: formData.type,
        category: formData.category,
        protocol_ids: selectedProtocols,
      };
      if (formData.type === 'mcq') {
        questionData.choices = formData.choices.filter(c => c.trim());
      }
      if (formData.type === 'boolean') {
        questionData.choices = ['Yes', 'No'];
      }
      if (formData.type === 'text') {
        questionData.choices = [];
      }
      if (formData.type === 'range') {
        questionData.choices = [];
      }
      if (formData.type === 'color_scale') {
        questionData.choices = ['red', 'orange', 'yellow', 'green'];
      }
      
      // Update question
      const response = await updateQuestion(id, questionData);
      
      // Check for successful response - handle different response formats
      const isSuccess = response.success ||
                       response.data?.success ||
                       (response.status >= 200 && response.status < 300) ||
                       (!response.error && !response.data?.error);
      
      if (isSuccess) {
        // Navigate back to questions list with success message
        navigate('/program-admin/questions/view-all', {
          state: { message: 'Question updated successfully' }
        });
      } else {
        // Extract error message from various possible locations
        const errorMessage = response.error ||
                            response.message ||
                            response.data?.error ||
                            response.data?.message ||
                            'Failed to update question';
        throw new Error(errorMessage);
      }
      
    } catch (error) {
      console.error('Error updating question:', error);
      
      // Extract error message from various possible locations
      let errorMessage = 'Failed to update question';
      
      if (error.response) {
        // Axios error with response
        errorMessage = error.response.data?.message ||
                      error.response.data?.error ||
                      `Server error: ${error.response.status}`;
      } else if (error.message) {
        // Standard error message
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      setIsSubmitting(false);
    }
  };
  
  const handleDelete = async () => {
    try {
      setIsSubmitting(true);
      setError('');
      const response = await deleteQuestion(id);
      if (response.data && response.data.success) {
        navigate('/program-admin/questions/view-all', {
          state: { message: 'Question made inactive successfully' }
        });
      } else {
        // Show backend error message if present
        throw new Error(
          (response.data && (response.data.message || response.data.error)) || 'Failed to make question inactive'
        );
      }
    } catch (error) {
      // PATCH: Show backend error message if available
      let msg = error.message || 'Failed to make question inactive';
      if (error.response && error.response.data) {
        msg = error.response.data.message || error.response.data.error || msg;
      }
      setError(msg);
      setIsSubmitting(false);
    }
  };
  
  const handleCancel = () => {
    navigate('/program-admin/questions/view-all');
  };
  
  return (
    <ResearchLayout pageTitle="Edit Question">
      <div className="container-fluid">
        {/* Breadcrumb */}
        <div className="row mb-4">
          <div className="col-12">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/program-admin/dashboard">
                    <i className="fas fa-home me-1"></i>
                    Dashboard
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/program-admin/questions/view-all">
                    <i className="fas fa-question-circle me-1"></i>
                    Questions
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Edit Question
                </li>
              </ol>
            </nav>
          </div>
        </div>
        
        {/* Error Display */}
        {error && (
          <div className="alert alert-danger mb-4">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}
        
        {/* Edit Question Form */}
        <div className="row justify-content-center">
          <div className="col-lg-8 col-md-10">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading question...</span>
                </div>
                <p className="mt-2">Loading question...</p>
              </div>
            ) : (
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-primary text-white">
                  <h5 className="mb-0">
                    <i className="fas fa-edit me-2"></i>
                    Edit Question
                  </h5>
                </div>
                <div className="card-body p-4">
                  <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                      <label htmlFor="text" className="form-label fw-bold">
                        <i className="fas fa-question-circle me-2 text-primary"></i>
                        Question Text *
                      </label>
                      <textarea
                        className="form-control"
                        id="text"
                        name="text"
                        rows="3"
                        value={formData.text}
                        onChange={handleInputChange}
                        placeholder="Enter your research question..."
                        required
                      ></textarea>
                    </div>
                    
                    <div className="row mb-4">
                      <div className="col-md-6 mb-3 mb-md-0">
                        <label htmlFor="type" className="form-label fw-bold">
                          <i className="fas fa-list-ul me-2 text-primary"></i>
                          Question Type *
                        </label>
                        <select
                          className="form-select"
                          id="type"
                          name="type"
                          value={formData.type}
                          onChange={handleInputChange}
                        >
                          <option value="mcq">Multiple Choice</option>
                          <option value="boolean">True/False</option>
                          <option value="text">Text</option>
                          <option value="range">Range (1-100)</option>
                          <option value="color_scale">Color Scale</option>
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label htmlFor="category" className="form-label fw-bold">
                          <i className="fas fa-tag me-2 text-primary"></i>
                          Category
                        </label>
                        <select
                          className="form-select"
                          id="category"
                          name="category"
                          value={formData.category}
                          onChange={handleInputChange}
                        >
                          <option value="General">General</option>
                          <option value="Medical History">Medical History</option>
                          <option value="Lifestyle">Lifestyle</option>
                          <option value="Mental Health">Mental Health</option>
                          <option value="Physical Health">Physical Health</option>
                        </select>
                      </div>
                    </div>
                    
                    {/* Multiple Choice Options */}
                    {formData.type === 'mcq' && (
                      <div className="mb-4">
                        <label className="form-label fw-bold">
                          <i className="fas fa-check-square me-2 text-primary"></i>
                          Answer Choices *
                        </label>
                        <div className="card bg-light">
                          <div className="card-body">
                            <p className="text-muted small mb-3">
                              Add at least 2 options for your multiple choice question.
                            </p>
                            {formData.choices.map((choice, index) => (
                              <div key={index} className="input-group mb-2">
                                <span className="input-group-text bg-white">
                                  {String.fromCharCode(65 + index)}
                                </span>
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder={`Option ${String.fromCharCode(65 + index)}`}
                                  value={choice}
                                  onChange={(e) => handleChoiceChange(index, e.target.value)}
                                />
                                <button
                                  type="button"
                                  className="btn btn-outline-danger"
                                  onClick={() => removeChoice(index)}
                                  disabled={formData.choices.length <= 2}
                                >
                                  <i className="fas fa-trash-alt"></i>
                                </button>
                              </div>
                            ))}
                            
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm mt-2"
                              onClick={addChoice}
                              disabled={formData.choices.length >= 8}
                            >
                              <i className="fas fa-plus me-1"></i>
                              Add Option
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Text Type Info */}
                    {formData.type === 'text' && (
                      <div className="mb-4">
                        <label className="form-label fw-bold">
                          <i className="fas fa-font me-2 text-primary"></i>
                          Text Answer (maximum 20 words)
                        </label>
                        <div className="form-text">
                          Respondents will answer with a short text (maximum 20 words).
                        </div>
                      </div>
                    )}
                    
                    {/* Range Type Info */}
                    {formData.type === 'range' && (
                      <div className="mb-4">
                        <label className="form-label fw-bold">
                          <i className="fas fa-sliders-h me-2 text-primary"></i>
                          Range Selection (1-100)
                        </label>
                        <div className="form-text">
                          Respondents will select a numeric value between 1 and 100 using a slider or input controls.
                        </div>
                      </div>
                    )}
                    
                    {/* Color Scale Type Info */}
                    {formData.type === 'color_scale' && (
                      <div className="mb-4">
                        <label className="form-label fw-bold">
                          <i className="fas fa-palette me-2 text-primary"></i>
                          Color Scale Selection
                        </label>
                        <div className="form-text mb-3">
                          Respondents will select from 4 color options: Red, Orange, Yellow, Green displayed as colored rectangles.
                        </div>
                        <div className="card bg-light">
                          <div className="card-body">
                            <p className="text-muted small mb-3">Preview of color options:</p>
                            <div className="d-flex gap-2 flex-wrap">
                              <div className="d-flex align-items-center justify-content-center"
                                   style={{
                                     backgroundColor: '#dc3545',
                                     color: 'white',
                                     padding: '10px 20px',
                                     borderRadius: '5px',
                                     fontWeight: 'bold',
                                     minWidth: '80px'
                                   }}>
                                Red
                              </div>
                              <div className="d-flex align-items-center justify-content-center"
                                   style={{
                                     backgroundColor: '#fd7e14',
                                     color: 'white',
                                     padding: '10px 20px',
                                     borderRadius: '5px',
                                     fontWeight: 'bold',
                                     minWidth: '80px'
                                   }}>
                                Orange
                              </div>
                              <div className="d-flex align-items-center justify-content-center"
                                   style={{
                                     backgroundColor: '#ffc107',
                                     color: 'black',
                                     padding: '10px 20px',
                                     borderRadius: '5px',
                                     fontWeight: 'bold',
                                     minWidth: '80px'
                                   }}>
                                Yellow
                              </div>
                              <div className="d-flex align-items-center justify-content-center"
                                   style={{
                                     backgroundColor: '#198754',
                                     color: 'white',
                                     padding: '10px 20px',
                                     borderRadius: '5px',
                                     fontWeight: 'bold',
                                     minWidth: '80px'
                                   }}>
                                Green
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Linked Protocols Multi-Select */}
                    <div className="mb-4">
                      <label className="form-label fw-bold">
                        <i className="fas fa-link me-2 text-info"></i>
                        Linked Protocols
                      </label>
                      <select
                        className="form-select"
                        multiple
                        value={selectedProtocols}
                        onChange={e =>
                          setSelectedProtocols(Array.from(e.target.selectedOptions, opt => Number(opt.value)))
                        }
                      >
                        {protocols.map(protocol => (
                          <option key={protocol.id} value={protocol.id}>
                            {protocol.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="d-flex justify-content-between mt-4">
                      <div>
                        <button
                          type="button"
                          className="btn btn-outline-danger me-2"
                          onClick={() => setDeleteConfirmation(true)}
                          disabled={isSubmitting}
                        >
                          <i className="fas fa-eye-slash me-2"></i>
                          Make Inactive
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={handleCancel}
                          disabled={isSubmitting}
                        >
                          <i className="fas fa-times me-2"></i>
                          Cancel
                        </button>
                      </div>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Saving...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save me-2"></i>
                            Save Changes
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <div className="modal fade show" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  Confirm Make Inactive
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setDeleteConfirmation(false)}
                  disabled={isSubmitting}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to make this question inactive? Inactive questions will not appear in the link questions view and cannot be used in new protocols.</p>
                
                {error && (
                  <div className="alert alert-danger">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {error}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setDeleteConfirmation(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDelete}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Making Inactive...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-eye-slash me-2"></i>
                      Make Inactive
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </ResearchLayout>
  );
};

export default ProgramAdminEditQuestion;